/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { IInspectorControl_Select, TInspectorControlProps } from "../InspectorControlTypes";
import { useBlueprintModel } from "../../../ui/hooks";
import { FieldGroup, FieldSelect, TSelectOption } from "@adapptio/lib-editor-ui";
import { useTranslate } from "@adapptio/lib-components";
import { isModelValueRequired } from "@adapptio/lib-blueprint";

export interface IInspectorControlSelectProps extends TInspectorControlProps<IInspectorControl_Select> {}

export const InspectorControlSelect: React.FC<IInspectorControlSelectProps> = ({ model: rootModel }) => {
	const { model: valueModel, validationErrors } = useBlueprintModel(
		rootModel.value.constant,
		"constString"
	);

	const required = isModelValueRequired(valueModel) || isModelValueRequired(rootModel);

	const t = useTranslate();

	const options = useMemo<TSelectOption[]>(
		() =>
			rootModel.schema.opts.constants.map((item) => ({
				label: t("editor", item.label),
				value: String(item.value)
			})),
		[ rootModel ]
	);

	const handleChange = useCallback(
		(value: string) => {
			if (!valueModel) {
				return;
			}

			valueModel.schema.setValue(valueModel, value, true);
		},
		[ valueModel ]
	);

	return (
		<FieldGroup grow>
			<FieldSelect
				validationErrors={validationErrors}
				allowCustomInput
				value={String(valueModel.value)}
				options={options}
				onChange={handleChange}
				clearable={!required}
				placeholder={valueModel.schema.opts.placeholder}
			/>
		</FieldGroup>
	);
};
